import { useContext, useEffect, useState } from 'react';
import {
  useDeactivateUserMutation,
  useReactivateUserMutation,
  useResendUserInviteMutation,
  useResetUserPasswordMutation,
  useUserHistoryQuery,
  useUserQuery,
} from '@common/cache';
import { toast } from 'react-toastify';
import { formatApiError } from '@common/errors/error-formatting.ts';
import { AdminPanelDrawerContext } from '@views/pages/Admin/context';

interface IUseUserItemProps {
  id: string;
  handleClose: () => void;
}

const historyTabIndex = '3';

export const useUserItem = ({ id, handleClose }: IUseUserItemProps) => {
  const [isResetPasswordOpen, setIsResetPasswordOpen] = useState(false);
  const [isResendInvitationOpen, setIsResendInvitationOpen] = useState(false);
  const [isDeactivateOpen, setIsDeactivateOpen] = useState(false);
  const [isConfirmDeactivateOpen, setIsConfirmDeactivateOpen] = useState(false);
  const [idForTransfer, setIdForTransfer] = useState<string>('');
  const [historyPage, setHistoryPage] = useState(1);
  const {
    isFormClean,
    handleClear,
    handleCloseTrigger,
    tabIndex,
    handleTriggerChangeTab,
    triggerClose,
  } = useContext(AdminPanelDrawerContext);

  const user = useUserQuery({
    user_id: id,
  });

  const userHistory = useUserHistoryQuery({
    user_id: id,
    page: historyPage,
    limit: 100,
    enabled: historyTabIndex === tabIndex,
  });

  const resetPasswordMutation = useResetUserPasswordMutation({ id });
  const resendInviteMutation = useResendUserInviteMutation({ id });
  const deactivateUserMutation = useDeactivateUserMutation({ id });
  const reactivateUserMutation = useReactivateUserMutation({ id });

  const isUserDeactivated = user.data?.status === 'deactivated';

  //Open modals
  const handleDeactivateOpen = () => {
    setIsDeactivateOpen(true);
  };
  const handleConfirmDeactivateOpen = (id: string) => {
    setIdForTransfer(id);
    setIsConfirmDeactivateOpen(true);
    setIsDeactivateOpen(false);
  };
  const handleResetPasswordOpen = () => {
    setIsResetPasswordOpen(true);
  };
  const handleResendInvitationOpen = () => {
    setIsResendInvitationOpen(true);
  };

  //Close modals
  const handleDeactivateClose = () => {
    setIsDeactivateOpen(false);
  };
  const handleConfirmDeactivateClose = () => {
    setIsConfirmDeactivateOpen(false);
  };
  const handleResetPasswordClose = () => {
    setIsResetPasswordOpen(false);
  };
  const handleResendInvitationClose = () => {
    setIsResendInvitationOpen(false);
  };

  //User mutations
  const confirmResetPassword = async (reset_mfa: boolean = false) => {
    try {
      await resetPasswordMutation.mutateAsync({ reset_mfa });
      toast.success('Password reset request sent');
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const confirmDeactivate = async () => {
    try {
      await deactivateUserMutation.mutateAsync(idForTransfer);
      toast.success('You deactivated this account!');
      handleConfirmDeactivateClose();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const confirmResendInvitation = async () => {
    try {
      await resendInviteMutation.mutateAsync();
      toast.success('User invite resend successful');
      handleResendInvitationClose();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const reactivateUser = async () => {
    try {
      await reactivateUserMutation.mutateAsync();
      toast.success('You reactivated this account!');
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  useEffect(() => {
    if (isFormClean && triggerClose) {
      handleClose();
      handleClear();
    }
  }, [isFormClean, triggerClose]);

  const isMutationPending =
    deactivateUserMutation.isPending ||
    resendInviteMutation.isPending ||
    resetPasswordMutation.isPending ||
    reactivateUserMutation.isPending;

  return {
    user: { ...user.data!, id },
    isMutationPending,
    tabIndex,
    handleTriggerChangeTab,
    userHistory: userHistory.data,
    isResetPasswordOpen,
    handleResetPasswordOpen,
    handleResetPasswordClose,
    confirmResetPassword,
    isResendInvitationOpen,
    handleResendInvitationOpen,
    handleResendInvitationClose,
    confirmResendInvitation,
    isDeactivateOpen,
    handleDeactivateOpen,
    handleDeactivateClose,
    confirmDeactivate,
    historyPage,
    setHistoryPage,
    handleCloseTrigger,
    isUserDeactivated,
    reactivateUser,
    handleConfirmDeactivateOpen,
    handleConfirmDeactivateClose,
    isConfirmDeactivateOpen,
    historyTabIndex,
  };
};
